import React, { Children, isValidElement } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Typography, Box, Divider, Paper } from '@mui/material';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { headingSlug } from '../slug';

function getTextContent(children: React.ReactNode): string {
  let text = '';
  Children.forEach(children, child => {
    if (typeof child === 'string') {
      text += child;
    } else if (isValidElement(child)) {
      const props = child.props as { children?: React.ReactNode };
      if (props.children) {
        text += getTextContent(props.children);
      }
    }
  });
  return text;
}

function getCalloutStyle(text: string) {
  const lower = text.toLowerCase();
  if (lower.startsWith('dica:') || lower.startsWith('exemplo:')) {
    return { border: '#79CF62', bg: '#f0faf0', icon: <TipsAndUpdatesIcon sx={{ fontSize: 18, color: '#79CF62', mt: '2px' }} /> };
  }
  if (lower.startsWith('atenção:') || lower.startsWith('importante:')) {
    return { border: '#e6a817', bg: '#fefce8', icon: <WarningAmberIcon sx={{ fontSize: 18, color: '#e6a817', mt: '2px' }} /> };
  }
  return { border: '#7869DF', bg: '#f5f3ff', icon: <InfoOutlinedIcon sx={{ fontSize: 18, color: '#7869DF', mt: '2px' }} /> };
}

const components = {
  // id via headingSlug do texto do heading: permite link direto pra seção (ManualLinkButton
  // `section` + ManualPage rolando até `location.hash`).
  h1: ({ children }: any) => (
    <Typography id={headingSlug(getTextContent(children))} variant="h4" fontWeight={700} mt={3} mb={2} color="#275030">{children}</Typography>
  ),
  h2: ({ children }: any) => (
    <Typography id={headingSlug(getTextContent(children))} variant="h5" fontWeight={600} mt={4} mb={1.5} color="#7869DF">{children}</Typography>
  ),
  h3: ({ children }: any) => (
    <Typography id={headingSlug(getTextContent(children))} variant="h6" fontWeight={600} mt={3} mb={1} color="#275030">{children}</Typography>
  ),
  p: ({ children }: any) => (
    <Typography variant="body1" mb={1.5} lineHeight={1.8}>{children}</Typography>
  ),
  li: ({ children }: any) => (
    <li><Typography variant="body1" component="span" lineHeight={1.8}>{children}</Typography></li>
  ),
  ol: ({ children }: any) => (
    <Box component="ol" sx={{ pl: 2, mb: 1.5, '& li': { mb: 0.5 } }}>{children}</Box>
  ),
  ul: ({ children }: any) => (
    <Box component="ul" sx={{ pl: 2, mb: 1.5, '& li': { mb: 0.5 } }}>{children}</Box>
  ),
  hr: () => <Divider sx={{ my: 3, borderColor: '#79CF62', opacity: 0.4 }} />,
  blockquote: ({ children }: any) => {
    const text = getTextContent(children);
    const style = getCalloutStyle(text);
    return (
      <Paper
        elevation={0}
        sx={{ borderLeft: `4px solid ${style.border}`, pl: 2, pr: 2, py: 1, my: 2, bgcolor: style.bg, display: 'flex', gap: 1 }}
      >
        <Box sx={{ flexShrink: 0, pt: 0.5 }}>{style.icon}</Box>
        <Box>{children}</Box>
      </Paper>
    );
  },
  strong: ({ children }: any) => (
    <strong style={{ color: '#275030' }}>{children}</strong>
  ),
  code: ({ children }: any) => (
    <code style={{ backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: 4, fontSize: '0.9em' }}>
      {children}
    </code>
  ),
  // Bloco de código rola dentro da caixa: linha longa (SQL das notas do Pedidos ao dev) não estoura a largura.
  pre: ({ children }: any) => (
    <Box component="pre" sx={{ overflowX: 'auto', bgcolor: '#f0f0f0', p: 1.5, borderRadius: 1, my: 1.5, '& code': { p: 0 } }}>{children}</Box>
  ),
  em: ({ children }: any) => (
    <em style={{ color: '#666', fontStyle: 'italic' }}>{children}</em>
  ),
  // Tabelas (regra RFM etc.) precisam do remark-gfm abaixo pra existir; sem ele o markdown de
  // tabela vira parágrafo com "|" solto.
  table: ({ children }: any) => (
    <Box sx={{ overflowX: 'auto', my: 2 }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.9em' }}>{children}</Box>
    </Box>
  ),
  th: ({ children }: any) => (
    <Box component="th" sx={{ border: '1px solid #e0e0e0', p: '6px 10px', bgcolor: '#f5f3ff', textAlign: 'left', fontWeight: 700, color: '#275030' }}>
      {children}
    </Box>
  ),
  td: ({ children }: any) => (
    <Box component="td" sx={{ border: '1px solid #e0e0e0', p: '6px 10px' }}>{children}</Box>
  ),
};

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => (
  <Box sx={{ maxWidth: 800 }}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{content}</ReactMarkdown>
  </Box>
);

export default MarkdownRenderer;
